import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {OrganizzazioniService} from './organizzazioni.service';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {SaveMessage} from '../_helpers/save-message';
import {Spedizione} from '../_models/Spedizione';
import {SearchResult} from '../_models/SearchResult';

@Injectable({providedIn: 'root'})
export class SpedizioniService {
  public handleRefresh = new Subject();
  public handleSave: Subject<number> = new Subject();

  public lastSpedizioneLaunched: Subject<Spedizione> = new Subject<Spedizione>();
  public lastSpedizioneCreated: BehaviorSubject<Spedizione> = new BehaviorSubject<Spedizione>(null);
  public lastSpedizioneUpdated: BehaviorSubject<Spedizione> = new BehaviorSubject<Spedizione>(null);
  public lastSpedizioneDeleted: BehaviorSubject<Spedizione> = new BehaviorSubject<Spedizione>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {}

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/spedizioni`, this.hubState);

    this.hubConnection.on('SendCreated', (spedizione: Spedizione) => {
      if (spedizione.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        const rifOdS = this.getRifOdS(spedizione);
        const message = rifOdS
          ? `Nuovo documento creato sull'ordine ${rifOdS}.`
          : 'Nuovo documento creato.';
        this.saveMessage.messageInfo(message);
        this.lastSpedizioneCreated.next(spedizione);
      }
    });

    this.hubConnection.on('SendUpdated', (spedizione: Spedizione) => {
      if (spedizione.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        const rifOdS = this.getRifOdS(spedizione);
        const message = rifOdS
          ? `Documento aggiornato sull'ordine ${rifOdS}.`
          : 'Documento aggiornato.';
        this.saveMessage.messageInfo(message);
        this.lastSpedizioneUpdated.next(spedizione);
      }
    });

    this.hubConnection.on('SendDeleted', (spedizione: Spedizione) => {
      if (spedizione.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        const rifOdS = this.getRifOdS(spedizione);
        const message = rifOdS
          ? `Documento eliminato sull'ordine ${rifOdS}.`
          : 'Documento eliminato.';
        this.saveMessage.messageInfo(message);
        this.lastSpedizioneDeleted.next(spedizione);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  getSpedizioni(
    orgId: number,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    contesto: number,
    codCliFor: number,
    rifDoc: string,
    stato: number,
    startDate: Date,
    endDate: Date) {
    return firstValueFrom(
      this.http.post<SearchResult<Spedizione>>(`${environment.localApiUrl}api/Trasferimento/Spedizioni/${orgId}`,
        {orgId, search, page, count, sortField, sortAsc, contesto, codCliFor, rifDoc, stato, startDate, endDate})
    );
  }

  async getSpedizione(orgId: number, idSpedizione: number) {
    return firstValueFrom(this.http.get<Spedizione>(`${environment.localApiUrl}api/Trasferimento/Spedizione/${orgId}/${idSpedizione}`));
  }

  async getSpedizioneByRifEsterno(orgId: number, rifEsterno: string) {
    return firstValueFrom(this.http.post<Spedizione>(`${environment.localApiUrl}api/Trasferimento/SpedizioneByRifEsterno/${orgId}`,
      {rifEsterno}));
  }

  async createSpedizione(spedizione: Spedizione) {
    return firstValueFrom(this.http.post<Spedizione>(`${environment.localApiUrl}api/Trasferimento/CreateSpedizione`, spedizione));
  }

  async updateSpedizione(spedizione: Spedizione) {
    return firstValueFrom(this.http.post<Spedizione>(`${environment.localApiUrl}api/Trasferimento/UpdateSpedizione`, spedizione));
  }

  async deleteSpedizione(orgId: number, idSpedizione: number) {
    return firstValueFrom(this.http.get<Spedizione>(`${environment.localApiUrl}api/Trasferimento/DeleteSpedizione?orgId=${orgId}&idSpedizione=${idSpedizione}`));
  }

  private getRifOdS(spedizione: Spedizione): string | null {
    if (!spedizione?.idDocumento || spedizione.idDocumento <= 0) {
      return null;
    }
    let rifOdS = `${spedizione.idDocumento}`;
    if (spedizione.idRiga > 0) {
      rifOdS += `.${spedizione.idRiga}`;
    }
    if (spedizione.idRigaLavorazione > 0) {
      rifOdS += `.${spedizione.idRigaLavorazione}`;
    }
    return rifOdS;
  }
}
